import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';

import Button from '@components/button/Button';
import Icon from '@components/icon/Icon';
import CustomTooltip from '@components/tooltip/Tooltip';

import './dropdownIcon.css';

interface DropDownActionButtonProps {
  label?: string;
  dropdownOptions?: Array<{
    id: string | number;
    label: string;
    value: string;
    isDisabled?: boolean;
    iconName?: string;
    disabledTooltip?: string;
  }>;
  onDropdownSelect?: (option: {
    id: string | number;
    label: string;
    value: string;
  }) => void;
  className?: string;
  disabled?: boolean;
  loading?: boolean;
  leftIconName?: string;
  variant?: 'primary' | 'tertiary';
  leftIconStyle?: string;
  leftIconColor?: string;
  labelStyle?: string;
  triggerStyles?: string;
  chevronColor?: 'white' | 'dark';
}

const DropDownActionButton: React.FC<DropDownActionButtonProps> = ({
  label,
  dropdownOptions = [],
  onDropdownSelect,
  className = '',
  disabled = false,
  loading = false,
  leftIconName,
  variant = 'primary',
  leftIconStyle = '!size-3',
  leftIconColor = 'white',
  labelStyle,
  triggerStyles,
  chevronColor = 'white'
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleDropdownToggle = () => {
    if (!disabled && !loading) {
      setIsDropdownOpen(!isDropdownOpen);
    }
  };

  const handleOptionClick = (option: {
    id: string | number;
    label: string;
    value: string;
  }) => {
    if (onDropdownSelect) {
      onDropdownSelect(option);
    }
    setIsDropdownOpen(false);
  };

  return (
    <div className={clsx('relative inline-flex', className)} ref={dropdownRef}>
      <Button
        handleClick={handleDropdownToggle}
        variant={variant}
        size="none"
        label={label}
        leftIconName={leftIconName}
        leftIconStyle={leftIconStyle}
        leftIconStroke={leftIconColor}
        labelStyle={labelStyle}
        className={clsx('py-3 pl-4 pr-2', triggerStyles)}
        disabled={disabled}
        loading={loading}
        rightIconName={
          chevronColor === 'dark' ? 'down-icon-dark' : 'down-icon-white'
        }
        rightIconStyle={clsx('ml-3', isDropdownOpen && 'rotate-180')}
      />

      {isDropdownOpen && dropdownOptions.length > 0 && (
        <div className="absolute right-0 top-full z-50 mt-1 min-w-70 rounded-2xl bg-white shadow-lg">
          <div className="flex flex-col gap-2 p-3">
            {dropdownOptions.map((option) => (
              <div
                key={option.id}
                data-tooltip-id={
                  option.isDisabled && option.disabledTooltip
                    ? `dropdown-${option.id}-tooltip`
                    : undefined
                }
              >
                <button
                  type="button"
                  onClick={() => handleOptionClick(option)}
                  disabled={option.isDisabled}
                  className={clsx(
                    'flex w-full items-center gap-2 rounded-lg px-4 py-3 text-left transition-colors hover:bg-grey10',
                    option.isDisabled
                      ? 'cursor-not-allowed text-cofeeNeutralInactive'
                      : 'text-content'
                  )}
                >
                  {option.iconName && (
                    <div className="flex size-5 items-center justify-center">
                      <Icon
                        name={option.iconName}
                        className={clsx(
                          'size-5',
                          option.isDisabled
                            ? 'iconStrokeGrey40'
                            : 'iconStrokeDarkGrey'
                        )}
                      />
                    </div>
                  )}
                  <div
                    className={`text-sm font-medium text-cofeeNeutral10 ${
                      option.isDisabled
                        ? '!text-cofeeNeutralInactive'
                        : 'text-cofeeNeutral10'
                    }`}
                  >
                    {option.label}
                  </div>
                </button>
                {option.isDisabled && option.disabledTooltip && (
                  <CustomTooltip
                    id={`dropdown-${option.id}-tooltip`}
                    type="text"
                    tooltipText={option.disabledTooltip}
                    place="top"
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DropDownActionButton;
